<template>
  <div class="block-dynamics-365">
    <div ref="el" />
    <div v-if="hasError">
      {{ $texts('dynamicsFormLoadError', 'The form could not be loaded.') }}
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  formId: string
  eventId?: string
  apiType?: string
}>()

const el = ref<HTMLDivElement>()
const hasError = ref(false)

// See this page for details how this untyped global window magic works:
// https://learn.microsoft.com/en-us/dynamics365/customer-insights/journeys/developer/realtime-marketing-form-client-side-extensibility#rendering-a-marketing-form-using-a-javascript-api
type WindowDynamic365MktForms = {
  createForm: (
    id: string,
    formApiBaseUrl: string,
    formUrl: string,
    args?: Record<string, string>,
  ) => HTMLDivElement | null
}

const { onLoaded } = useScript(
  {
    // This shit is a mind boggling 8.1 megabyte in size.
    src: 'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/eur/FormLoader/FormLoader.bundle.js',
    crossorigin: false,
    async: true,
    defer: true,
  },
  {
    use() {
      // This is called once the script has been loaded.
      // We can return whatever crap the script has injected into window here.
      return window.d365mktforms as WindowDynamic365MktForms
    },
  },
)

const computedApiUrlType = computed<string>(() => {
  return props.apiType ?? 'landingpageforms'
})

onLoaded((forms) => {
  if (!el.value) {
    return
  }

  try {
    // See the linked page above for "how this works".
    // It basically renders the form as a DOM element.
    const createdForm = forms.createForm(
      props.formId,
      `https://public-eur.mkt.dynamics.com/api/v1.0/orgs/eaf5611e-f81c-4608-b97e-01419439940e/${computedApiUrlType.value}`,
      `https://assets-eur.mkt.dynamics.com/eaf5611e-f81c-4608-b97e-01419439940e/digitalassets/forms/${props.formId}`,
      {
        ...(props.eventId ? { 'data-readable-event-id': props.eventId } : {}),
      },
    )

    // We have to assume that it can also return nothing.
    if (!createdForm) {
      hasError.value = true
      return
    }

    // Append the form to our component.
    el.value.appendChild(createdForm)
  } catch {
    hasError.value = true
  }
})
</script>

<style lang="postcss">
.block-dynamics-365 {
  .marketingForm * {
    @apply font-sans;
  }
  .marketingForm {
    label,
    p,
    p *,
    label *,
    span {
      @apply !font-sans !text-scheme-invert;
    }
  }
  [data-layout='true'] {
    @apply !m-0;
  }

  .columnContainer,
  .optionSetFormFieldBlock,
  .phoneFormFieldBlock,
  .twoOptionFormFieldBlock {
    @apply !px-0 !pt-0 !pb-size-4-5;
  }
  .wrap-section {
    @apply !pb-0;
  }
  .optionSetFormFieldBlock {
    @apply !pt-size-5;
  }
  [data-editorblocktype='Text'] {
    @apply !m-0 !text-left !text-size-5;

    h1 {
      @apply mb-size-5 !text-size-4 !font-bold;
    }
    p {
      @apply !text-size-5;
    }
  }
  .textFormFieldBlock,
  .phoneFormFieldBlock,
  .lookupFormFieldBlock,
  .optionSetFormFieldBlock {
    @apply flex flex-col gap-0 p-0 pb-size-4-5;

    label {
      @apply mb-2 block text-size-6 font-bold text-scheme-invert;

      a {
        @apply mx-1 my-0;
      }

      &:not(:last-child) {
        @apply mb-2;
      }

      &::after {
        @apply text-scheme-invert;
      }
    }

    input,
    textarea,
    select {
      @apply block h-[3em] w-full rounded-none border-2 bg-scheme-main p-2 px-[14px] py-[10px] text-base text-button-color outline-border-hover;
    }

    .ui-menu-item {
      @apply text-base;
      &:hover,
      &:focus {
        @apply bg-primary;
      }
    }
  }

  .optionSetFormFieldBlock {
    .radiobuttons {
      & > div {
        @apply mb-size-8;
      }
      input {
        @apply border-primary accent-primary;
      }
      label {
        @apply !font-sans !text-size-6;
      }
    }
  }

  .phoneFormFieldBlock {
    .phoneCountryCode {
      @apply border-2 border-button-border-color hover:border-button-hover-border-color focus:border-button-hover-border-color;
    }
    .phoneCountryCodeLabel {
      @apply !text-black;
    }
    input {
      @apply border-none;
    }
  }

  .columnContainer {
    @apply !p-0;
    .inner {
      @apply !pb-0;
    }

    textarea {
      @apply bg-background text-scheme-invert;
    }
  }

  .optionSetFormFieldBlock {
    @apply !pt-0;
    & label {
      @apply !text-scheme-invert;
    }
  }

  .consentBlock {
    @apply !p-0 mt-size-6;

    > div {
      @apply items-start;
    }
  }

  .twoOptionFormFieldBlock {
    & + .consentBlock {
      @apply mt-0;
    }
  }

  .twoOptionFormFieldBlock div.twooption_checkbox div label,
  .twoOptionFormFieldBlock div.twooption_checkbox div label div,
  .consentBlock div label p {
    @apply !text-size-6;
  }

  .submitButtonWrapper {
    @apply !p-0 text-left !text-white;

    .submitButton {
      @apply bg-primary rounded-none;

      span {
        @apply !text-white;
      }
    }
  }
}
</style>
